import React, { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  Button,
  Tabs,
  Table,
  DatePicker,
  Dropdown,
  Select,
  SelectOption,
  Input,
  Icon,
} from 'ui';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import dayjs from 'dayjs';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import FileDownload from 'js-file-download';
import { MenuProps } from 'antd/es/menu';
import EditFarmDrawer from 'ui/Drawer/Drawers/EditFarmDrawer';
import { EditFarm } from 'admin/components/EditFarm';
import { FarmsProps } from 'interfaces/BackendModels';
import { publicPath } from 'enums/publicRoutes';
import { print } from '../../../../utils/print';
import { copyTable } from '../../../../utils/copyTable';
import { DeleteModal } from 'ui/Modal/DeleteModal/DeleteModal';
import { StyledFarmsPage } from './style';

const CONTRACT_TYPES = ['E-MARKET', 'WHOLESALE', 'DYNAMIC'] as const;

interface Record extends FarmsProps {
  id: number;
  website: string;
}

const FarmListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState({});
  const [paginationStatus, setPaginationStatus] = useState<any>({});
  const [reloadNumber, setReloadNumber] = useState(0);
  const [isEditFarmDrawerOpen, setEditFarmDrawer] = useState<boolean>(false);
  const [farmToEdit, setFarmToEdit] = useState<FarmsProps>({} as FarmsProps);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [farmToDelete, setFarmToDelete] = useState<number | undefined>(
    undefined
  );

  const intl = useIntl();
  const tableRef = React.useRef<HTMLDivElement>(null);

  useQueryApiClient({
    request: {
      url: `api/v2/farms/syncProductRemainders`,
      method: 'POST',
    },
  });

  const { appendData: exportData } = useQueryApiClient({
    request: {
      url: `api/v2/farms/export`,
      data: {
        filter: advancedSearch,
      },
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response, format) => {
      FileDownload(
        response,
        `${format}_${dayjs().format('YYYY-MM-DD')}.${format}`
      );
    },
  });

  const handleEdit = (record: Record) => {
    setFarmToEdit(record);
    setEditFarmDrawer(true);
  };

  const handleDelete = (record: Record) => {
    setFarmToDelete(record.id);
    setShowDeleteModal(true);
  };

  const handleOpenWeb = (record: Record) => {
    window.open(record.website, '_blank');
  };

  const handleOpenProfile = (record: Record) => {
    window.open(
      `${window.runConfig.frontendUrl}${publicPath.farm(record.slug, record.id)}`,
      '_blank'
    );
  };

  const handleCloseEditFarm = () => {
    setReloadNumber((prevState) => prevState + 1);
    setEditFarmDrawer(false);
  };

  function onActionSelect(action: string, record: Record) {
    switch (action) {
      case 'edit':
        handleEdit(record);
        break;
      case 'delete':
        handleDelete(record);
        break;
      case 'open_web':
        handleOpenWeb(record);
        break;
      case 'open_profile':
        handleOpenProfile(record);
        break;
    }
  }

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.id' }),
      dataIndex: 'id',
    },
    {
      title: intl.formatMessage({ id: 'farms.name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'farms.reg_nr' }),
      dataIndex: 'reg_nr',
    },
    {
      title: intl.formatMessage({ id: 'farms.pvd_nr' }),
      dataIndex: 'pvd_nr',
    },
    {
      title: intl.formatMessage({ id: 'farms.status' }),
      dataIndex: 'public',
      render: (value: boolean, record: Record) => {
        if (value) {
          return intl.formatMessage({ id: 'farms.public' });
        } else {
          return intl.formatMessage({ id: 'farms.hidden' });
        }
      },
    },
    {
      title: intl.formatMessage({ id: 'general.created_at' }),
      dataIndex: 'created_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.updated_at' }),
      dataIndex: 'updated_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      render: (record: Record) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: intl.formatMessage({ id: 'general.edit' }),
          },
          {
            key: 'delete',
            label: intl.formatMessage({ id: 'general.delete' }),
          },
          ...(record.website
            ? [
                {
                  key: 'open_web',
                  label: intl.formatMessage({ id: 'general.open_web' }),
                },
              ]
            : []),
          {
            key: 'open_profile',
            label: intl.formatMessage({ id: 'general.open_profile' }),
          },
        ];
        const onMenuClick: MenuProps['onClick'] = (e) => {
          onActionSelect(e.key, record);
        };

        return (
          <Dropdown menu={{ items, onClick: onMenuClick }} trigger={['click']}>
            <Button
              label={intl.formatMessage({ id: 'general.select_action' })}
            />
          </Dropdown>
        );
      },
    },
  ];

  const items = [
    {
      key: 'active_farms',
      label: intl.formatMessage({ id: 'navigation.farms' }),
      children: (
        <>
          <Filter
            onFinish={setAdvancedSearch}
            totalRecord={paginationStatus.total}
          >
            <DatePicker
              label={intl.formatMessage({ id: 'general.date_from' })}
              name="date_from"
            />
            <DatePicker
              label={intl.formatMessage({ id: 'general.date_to' })}
              name="date_to"
            />
            <Input
              label={intl.formatMessage({ id: 'general.search' })}
              name="search"
            />
            <Select
              label={intl.formatMessage({ id: 'farms.status' })}
              name="public"
              labelInValue
            >
              <SelectOption key={1} value={1}>
                {intl.formatMessage({ id: 'farms.public' })}
              </SelectOption>
              <SelectOption key={0} value={0}>
                {intl.formatMessage({ id: 'farms.hidden' })}
              </SelectOption>
            </Select>
            <Select
              label={intl.formatMessage({ id: 'farms.active_contract' })}
              name="contract_type"
              labelInValue
              allowClear
            >
              {CONTRACT_TYPES.map((type) => (
                <SelectOption key={type} value={type}>
                  {intl.formatMessage({ id: `contracts.${type}` })}
                </SelectOption>
              ))}
            </Select>
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.select' })}
              htmlType="submit"
            />
          </Filter>
          <div ref={tableRef}>
            <Table
              url="/api/v2/getAdminFarms"
              columns={columns}
              filter={advancedSearch}
              options={{ onlyFarms: '1' }}
              reload={reloadNumber}
              paginnationStatus={setPaginationStatus}
            />
          </div>
        </>
      ),
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'xlsx' || e.key === 'csv') {
      exportData({ format: e.key }, [], e.key);
    }

    if (e.key === 'print') {
      print(intl.formatMessage({ id: 'general.printing_header' }), 6);
    }

    if (e.key === 'copy') {
      copyTable(tableRef.current);
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      label: intl.formatMessage({ id: 'general.print' }),
      key: 'print',
    },
    {
      label: intl.formatMessage({ id: 'general.copy' }),
      key: 'copy',
    },
    {
      label: intl.formatMessage({ id: 'general.excel' }),
      key: 'xlsx',
    },
    {
      label: intl.formatMessage({ id: 'general.csv' }),
      key: 'csv',
    },
  ];

  const menuProps = {
    items: dropdownItems,
    onClick: handleMenuClick,
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.farms' }),
        },
      ]}
    >
      <DefaultLayout.PageContent>
        <StyledFarmsPage>
          <Tabs
            tabBarExtraContent={
              <StyledButtonList>
                <Dropdown menu={menuProps}>
                  <Button
                    label={intl.formatMessage({ id: 'general.export' })}
                    type="primary"
                  />
                </Dropdown>
                {/*================================================*/}
                {/*Disabled for now - NEEDS Manslauks updates*/}
                {/*================================================*/}

                {/*<Button*/}
                {/*  label={intl.formatMessage({*/}
                {/*    id: 'farms.sync_product_remainders',*/}
                {/*  })}*/}
                {/*  type="primary"*/}
                {/*  onClick={() => syncProductRemainders()}*/}
                {/*  loading={isLoading}*/}
                {/*  ability={'farm_edit'}*/}
                {/*/>*/}
              </StyledButtonList>
            }
            items={items}
          />
          <EditFarmDrawer
            setEditFarmDrawer={handleCloseEditFarm}
            isEditFarmDrawerOpen={isEditFarmDrawerOpen}
            destroyOnClose={true}
            closeIcon={<Icon name={'global-close-icon'} />}
          >
            <EditFarm
              farmId={farmToEdit.id}
              setEditFarmDrawer={handleCloseEditFarm}
            />
          </EditFarmDrawer>
          <DeleteModal
            setShowModal={setShowDeleteModal}
            showModal={showDeleteModal}
            deleteId={farmToDelete}
            url={'api/v2/farms/{id}'}
            setRefresh={setReloadNumber}
            confirmText={'message.confirm_delete_farm'}
            method={'DELETE'}
          />
        </StyledFarmsPage>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default FarmListPage;
